import { useCallback, useEffect, useMemo, useRef } from 'react';
import { parseOhlcLiveResponse, intradayHistoryDays } from '../utils/eodChartMath';
import { isMarketOpen, todayISO } from '../utils/marketUtils';
import { mergeBarSeries, isSessionAlignedTime } from '../utils/ohlcBarEngine';
import { useTmrSocket } from './useTmrSocket';

/**
 * Adaptive poll cadence (silent HTTP safety-net — never shown as a UI badge).
 * When WebSocket LTP is live → slower resync poll.
 * When WS down / market open → ~1s (near real-time LTP via HTTP live=1).
 */
export function getIntradayPollMs(intervalMinutes, { socketLive = false } = {}) {
  if (!isMarketOpen()) return 30000;
  if (socketLive) {
    // WS carries ~1s forming ticks; HTTP only resyncs history occasionally
    return 12000;
  }
  // No WS: poll as fast as practical for forming bar (Upstox LTP on server)
  if (intervalMinutes <= 1) return 1000;
  if (intervalMinutes <= 2) return 1000;
  if (intervalMinutes <= 5) return 1500;
  return 2000;
}

function candlesFingerprint(candles) {
  if (!candles?.length) return '';
  const last = candles[candles.length - 1];
  return `${candles.length}|${last.date}|${last.open}|${last.high}|${last.low}|${last.close}`;
}

/**
 * Convert a Socket.IO live:ohlc candle payload into board row format
 * (date = unix with IST offset used by lightweight-charts).
 */
export function socketCandleToRow(candle, tradeDate, intervalMinutes = 1) {
  if (!candle) return null;
  const day = candle.date || tradeDate || todayISO();
  // Reject out-of-session labels (night / post-close garbage) before they hit the chart.
  if (candle.label && !isSessionAlignedTime(candle.label, intervalMinutes)) {
    return null;
  }
  let unixSec = Number(candle.time) || 0;
  if (!unixSec && candle.label) {
    try {
      const ts = new Date(`${day}T${candle.label}:00+05:30`);
      if (!Number.isNaN(ts.getTime())) {
        unixSec = Math.floor(ts.getTime() / 1000) + 19800;
      }
    } catch (_) { /* noop */ }
  }
  if (!unixSec) return null;
  // Defence: IST-as-UTC chart time must still fall inside regular session.
  const d = new Date(unixSec * 1000);
  const mins = d.getUTCHours() * 60 + d.getUTCMinutes();
  if (!isSessionAlignedTime(mins, intervalMinutes)) return null;
  return {
    date: unixSec,
    open: Number(candle.open),
    high: Number(candle.high),
    low: Number(candle.low),
    close: Number(candle.close),
    volume: Number(candle.volume || 0),
  };
}

/**
 * Merge a single live candle into an existing series (update last or append).
 * Uses the shared OHLC bar engine — no duplicates, no incomplete bars, no reverse writes.
 */
export function mergeLiveCandle(prevCandles, row) {
  return mergeBarSeries(prevCandles, row);
}

/**
 * Continuous intraday OHLC updates for index board charts.
 * Primary: Socket.IO live:ohlc with candle payloads (~1s LTP + closed bars).
 * Fallback: adaptive polling of api_ohlc_live.php.
 */
export function useLiveBoardOhlc({
  enabled,
  symbols,
  tradeDate,
  intervalMinutes = 5,
  setRawBySym,
  onSourceChange,
  onStatusChange,
  /** When true, also seed missing symbols via HTTP (not only update). */
  seedIfEmpty = true,
}) {
  const inflightRef = useRef(false);
  const failCountRef = useRef(0);
  const socketLiveRef = useRef(false);
  const lastSuccessRef = useRef(0);
  const pollIdRef = useRef(null);
  const staleIdRef = useRef(null);

  const liveSymbols = useMemo(
    () => (symbols || []).filter((s) => s.intradayApi),
    [symbols],
  );
  const socketSymbols = useMemo(
    () => liveSymbols.map((s) => s.intradayApi),
    [liveSymbols],
  );

  const reportStatus = useCallback((patch) => {
    onStatusChange?.(patch);
  }, [onStatusChange]);

  const applySocketCandle = useCallback((update) => {
    if (!update?.candle) return false;
    const sym = (update.symbol || '').toUpperCase();
    const match = liveSymbols.find((s) => s.intradayApi === sym);
    if (!match) return false;

    // Only apply if interval matches (or server omitted interval = generic nudge)
    if (update.interval != null && Number(update.interval) !== Number(intervalMinutes)) {
      return false;
    }

    const row = socketCandleToRow(update.candle, tradeDate || todayISO(), intervalMinutes);
    if (!row) return false;

    setRawBySym((prev) => {
      const cur = prev[match.id] || [];
      // If we have no history yet, don't invent a 1-bar chart — wait for HTTP seed
      if (!cur.length && seedIfEmpty) return prev;
      const merged = mergeLiveCandle(cur, row);
      if (merged === cur) return prev;
      if (candlesFingerprint(merged) === candlesFingerprint(cur)) return prev;
      return { ...prev, [match.id]: merged };
    });

    lastSuccessRef.current = Date.now();
    onSourceChange?.(update.via || 'ws', { via: update.via || 'ws', socketLive: true });
    return true;
  }, [liveSymbols, intervalMinutes, tradeDate, setRawBySym, onSourceChange, seedIfEmpty]);

  const refreshSymbol = useCallback(async (sym) => {
    const date = tradeDate || todayISO();
    const days = intradayHistoryDays(intervalMinutes);
    const res = await fetch(
      `/api/api_ohlc_live.php?symbol=${encodeURIComponent(sym.intradayApi)}&date=${encodeURIComponent(date)}&interval=${intervalMinutes}&live=1&days=${days}`,
      { credentials: 'include', cache: 'no-store' },
    );
    if (!res.ok) throw new Error(`${sym.label}: live fetch failed (${res.status})`);
    const payload = await res.json();
    const candles = parseOhlcLiveResponse(payload, payload.date || date);
    return { id: sym.id, data: candles, source: payload.source || 'live', count: candles.length };
  }, [tradeDate, intervalMinutes]);

  const refreshAll = useCallback(async () => {
    if (!enabled || inflightRef.current) return;
    if (!liveSymbols.length) return;

    inflightRef.current = true;
    // Silent HTTP path — do not surface "polling" to the UI
    try {
      const results = await Promise.all(liveSymbols.map(refreshSymbol));
      failCountRef.current = 0;
      lastSuccessRef.current = Date.now();

      setRawBySym((prev) => {
        let changed = false;
        const next = { ...prev };
        results.forEach((r) => {
          if (!r.data?.length) return;
          // Prefer full history replace when longer or empty; otherwise merge last bar only
          const prevBars = prev[r.id] || [];
          if (!prevBars.length || r.data.length >= prevBars.length) {
            const fp = candlesFingerprint(r.data);
            const prevFp = candlesFingerprint(prevBars);
            if (fp !== prevFp) {
              next[r.id] = r.data;
              changed = true;
            }
          } else {
            // Shorter response shouldn't wipe multi-day history — update last bar
            const last = r.data[r.data.length - 1];
            const merged = mergeLiveCandle(prevBars, last);
            if (candlesFingerprint(merged) !== candlesFingerprint(prevBars)) {
              next[r.id] = merged;
              changed = true;
            }
          }
        });
        return changed ? next : prev;
      });

      if (results.length && onSourceChange) {
        onSourceChange(results[0].source, {
          via: socketLiveRef.current ? 'ws' : 'http',
          socketLive: socketLiveRef.current,
        });
      }

      reportStatus({
        // Keep internal mode for diagnostics; UI maps via resolveDataSource (no POLLING badge)
        mode: socketLiveRef.current ? 'live' : 'live',
        socketLive: socketLiveRef.current,
        lastSuccess: Date.now(),
        error: null,
        source: results[0]?.source || null,
      });
    } catch (err) {
      failCountRef.current += 1;
      // Silent recovery — only mark offline after repeated failures while market open
      const offline = failCountRef.current >= 5 && !socketLiveRef.current && isMarketOpen();
      reportStatus({
        mode: offline ? 'offline' : (socketLiveRef.current ? 'live' : 'live'),
        socketLive: socketLiveRef.current,
        error: offline ? (err?.message || 'Update failed') : null,
        lastAttempt: Date.now(),
        lastSuccess: lastSuccessRef.current || null,
      });
    } finally {
      inflightRef.current = false;
    }
  }, [enabled, liveSymbols, refreshSymbol, setRawBySym, onSourceChange, reportStatus]);

  const armPollRef = useRef(() => {});

  const onSocketStatus = useCallback((st) => {
    const wasLive = socketLiveRef.current;
    const live = st.socket === 'connected' || st.phase === 'connected' || st.mode === 'live';
    socketLiveRef.current = live;

    // Re-arm silent poll cadence when connection state flips
    armPollRef.current?.();

    // Transient reconnect/connect — silent (no UI badge). Keep lastSuccess for Delayed logic.
    if (
      st.mode === 'reconnecting'
      || st.socket === 'reconnecting'
      || st.phase === 'reconnecting'
      || st.phase === 'connecting'
    ) {
      reportStatus({
        mode: 'live',
        socketLive: false,
        socket: st.socket || st.phase,
        error: null,
        lastSuccess: lastSuccessRef.current || null,
      });
      return;
    }

    if (live) {
      reportStatus({
        mode: 'live',
        socketLive: true,
        socket: 'connected',
        error: null,
        lastSuccess: lastSuccessRef.current || Date.now(),
      });
      // Seamless resume: resync history once after recovery
      if (!wasLive) {
        refreshAll();
      }
      return;
    }

    if (st.mode === 'offline' || st.socket === 'offline' || st.phase === 'offline') {
      // Only surface offline when we also have no recent data during market hours
      const age = lastSuccessRef.current ? Date.now() - lastSuccessRef.current : Infinity;
      reportStatus({
        mode: isMarketOpen() && age > 30000 ? 'offline' : 'live',
        socketLive: false,
        socket: 'offline',
        error: null,
        lastSuccess: lastSuccessRef.current || null,
      });
      return;
    }

    // disconnected / idle → silent HTTP fallback (still "live" data source if bars flow)
    reportStatus({
      mode: 'live',
      socketLive: false,
      socket: st.socket || st.phase || 'disconnected',
      error: null,
      lastSuccess: lastSuccessRef.current || null,
    });
  }, [reportStatus, refreshAll]);

  const onOhlc = useCallback((update) => {
    if (!update) return;
    const applied = applySocketCandle(update);
    if (!applied) {
      const sym = (update.symbol || '').toUpperCase();
      if (sym && !liveSymbols.some((s) => s.intradayApi === sym)) return;
      // Notification-only or empty series — soft full refresh
      refreshAll();
    } else {
      lastSuccessRef.current = Date.now();
      if (onSourceChange) {
        onSourceChange(update.via || 'ws', {
          via: update.via || 'ws',
          socketLive: true,
        });
      }
      reportStatus({
        mode: 'live',
        socketLive: true,
        via: update.via || 'ws',
        lastSuccess: lastSuccessRef.current,
        error: null,
      });
    }
  }, [applySocketCandle, liveSymbols, refreshAll, reportStatus, onSourceChange]);

  useTmrSocket({
    enabled: !!enabled && liveSymbols.length > 0,
    symbols: socketSymbols,
    onStatus: onSocketStatus,
    onOhlc,
  });

  // Adaptive polling + stale detector (Delayed)
  useEffect(() => {
    if (!enabled || !liveSymbols.length) {
      reportStatus({ mode: 'idle', error: null });
      return undefined;
    }

    refreshAll();

    const arm = () => {
      if (pollIdRef.current) clearInterval(pollIdRef.current);
      const ms = getIntradayPollMs(intervalMinutes, { socketLive: socketLiveRef.current });
      pollIdRef.current = setInterval(refreshAll, ms);
    };
    armPollRef.current = arm;
    arm();
    const cadenceId = setInterval(arm, 15000);

    // Data quality only: Delayed / Offline when feed stalls mid-session (no reconnecting chrome)
    staleIdRef.current = setInterval(() => {
      if (!isMarketOpen()) return;
      const age = Date.now() - (lastSuccessRef.current || 0);
      if (lastSuccessRef.current && age > 8000) {
        reportStatus({
          mode: age > 45000 && !socketLiveRef.current ? 'offline' : 'delayed',
          socketLive: socketLiveRef.current,
          lastSuccess: lastSuccessRef.current,
          error: null,
        });
      }
    }, 2000);

    return () => {
      if (pollIdRef.current) clearInterval(pollIdRef.current);
      if (staleIdRef.current) clearInterval(staleIdRef.current);
      clearInterval(cadenceId);
      armPollRef.current = () => {};
    };
  }, [enabled, liveSymbols, refreshAll, intervalMinutes, reportStatus]);

  return { refreshAll };
}

/** Intraday charts always stream today's session (plus multi-day history load). */
export function shouldUseLiveOhlc(intradayMode) {
  return intradayMode;
}
